/**
 * Monedas y Tasa del Día — catálogo de monedas del tenant y registro de la
 * tasa operativa (multi-moneda, hito 1). Vive dentro del hub de
 * Configuración; programa de rolesusuarios: `monedas`.
 *
 * Dos cosas que se parecen y NO son lo mismo, por eso salen juntas en
 * pantalla con su explicación:
 *   - Tasa del día: la tasa a la que se opera hoy. Se registra una vez al
 *     día (índice único por moneda y fecha en el backend: volver a
 *     registrarla corrige la del día, no acumula). Es lo nuevo.
 *   - Tasa mínima (varinicial.tasaus): el piso por debajo del cual ventas x
 *     mayor rechaza una factura. Es lo que ya existía, se edita en
 *     Configuración > Sistema y aquí solo se muestra como referencia.
 *
 * El catálogo nace con RD$ como base y USD apagada: mientras nadie habilite
 * una segunda moneda, el resto del sistema no ve ningún cambio.
 *
 * La base SE PUEDE cambiar (una empresa que opera en dólares pone USD), pero
 * detrás de una confirmación escrita: no convierte ningún monto guardado, así
 * que solo es correcto al poner en marcha el tenant. Ver establecer_base() en
 * app/monedas.py.
 */
import React, { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, SettingSection, SettingRow } from "components/financepro";
import { useApi } from "lib/api";
import { fmtMoney, fmtTasa, tasaInversa, toNum } from "lib/moneda";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});

// Dentro del hub el marco lo pone el panel derecho (mismo criterio que el
// resto de pantallas de Configuración).
const sectionBoxHub = { mb: 3 };
const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const hoyISO = () => new Date().toISOString().slice(0, 10);

const fechaDMY = (iso) => {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso || "");
};

export default function MonedasConfig({ enHub } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [monedas, setMonedas] = useState([]);
  const [tasas, setTasas] = useState({});
  const [tasaMinima, setTasaMinima] = useState(0);
  const [cargando, setCargando] = useState(true);

  // Valor tecleado por moneda para la tasa de hoy, y guardado en curso.
  const [borrador, setBorrador] = useState({});
  const [guardando, setGuardando] = useState("");

  const [historico, setHistorico] = useState({ codigo: "", filas: [] });

  // Cambio de moneda base: moneda elegida, texto de confirmación y guardado.
  const [cambioBase, setCambioBase] = useState(null);
  const [confirmaTexto, setConfirmaTexto] = useState("");
  const [cambiandoBase, setCambiandoBase] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/monedas_listar", {});
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo leer el catálogo", color: "error" });
        return;
      }
      setMonedas(data.monedas || []);
      setTasas(data.tasas || {});
      setTasaMinima(toNum(data.tasaminima));
    } finally {
      setCargando(false);
    }
  }, [post, showAlert]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const activas = monedas.filter((m) => m.activo === "S");
  const secundarias = activas.filter((m) => m.es_base !== "S");
  const base = monedas.find((m) => m.es_base === "S");
  const codigoBase = base?.codigo || "DOP";
  const simboloBase = base?.simbolo || "RD$";

  const verHistorico = async (codigo) => {
    const { ok, data } = await post("/api/tasas_historico", { codigo });
    if (!ok) {
      showAlert({ message: data.Error || "No se pudo leer el histórico", color: "error" });
      return;
    }
    setHistorico({ codigo, filas: data.tasas || [] });
  };

  const registrarTasa = async (codigo) => {
    const valor = toNum(borrador[codigo]);
    if (valor <= 0) {
      showAlert({ message: "La tasa debe ser mayor que cero", color: "warning" });
      return;
    }
    setGuardando(codigo);
    try {
      const { ok, data } = await post("/api/tasas_registrar", {
        codigo,
        tasa: valor,
        fecha: hoyISO(),
      });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo registrar la tasa", color: "error" });
        return;
      }
      setTasas(data.tasas || {});
      setBorrador((prev) => ({ ...prev, [codigo]: "" }));
      showAlert({ message: data.mensajeok || "Tasa registrada", color: "success" });
      if (historico.codigo === codigo) verHistorico(codigo);
    } finally {
      setGuardando("");
    }
  };

  const cambiarActivo = async (fila, activo) => {
    const { ok, data } = await post("/api/monedas_guardar", {
      codigo: fila.codigo,
      nombre: fila.nombre,
      simbolo: fila.simbolo,
      decimales: fila.decimales,
      activo: activo ? "S" : "N",
    });
    if (!ok) {
      showAlert({ message: data.Error || "No se pudo guardar la moneda", color: "error" });
      return;
    }
    setMonedas(data.monedas || []);
    showAlert({ message: data.mensajeok || "Moneda guardada", color: "success" });
    // Habilitar una moneda estrena su bloque de tasa: hay que traerla.
    cargar();
  };

  const cerrarCambioBase = () => {
    setCambioBase(null);
    setConfirmaTexto("");
  };

  const aplicarCambioBase = async () => {
    if (!cambioBase) return;
    setCambiandoBase(true);
    try {
      const { ok, data } = await post("/api/monedas_base", {
        codigo: cambioBase.codigo,
        confirmar: true,
      });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo cambiar la moneda base", color: "error" });
        return;
      }
      setMonedas(data.monedas || []);
      setTasas(data.tasas || {});
      showAlert({ message: data.mensajeok || "Moneda base actualizada", color: "success" });
      cerrarCambioBase();
      // La base cambió: las tasas y el histórico en pantalla ya no aplican.
      setHistorico({ codigo: "", filas: [] });
      cargar();
    } finally {
      setCambiandoBase(false);
    }
  };

  const bloqueTasa = (m) => {
    const info = tasas[m.codigo] || { tasa: 0, fecha: null, usuario: "", vigente: false };
    const sinTasa = toNum(info.tasa) <= 0;
    const desactualizada = !sinTasa && !info.vigente;

    return (
      <Box key={m.codigo} sx={{ py: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
                {m.codigo} · {m.nombre}
              </Typography>
              {sinTasa && (
                <Chip
                  size="small"
                  icon={<WarningAmberOutlinedIcon />}
                  label="Sin tasa registrada"
                  sx={{
                    bgcolor: FP.color.warningSoft,
                    color: "#92400E",
                    fontWeight: 700,
                    "& .MuiChip-icon": { color: "#92400E" },
                  }}
                />
              )}
              {desactualizada && (
                <Chip
                  size="small"
                  icon={<WarningAmberOutlinedIcon />}
                  label={`Del ${fechaDMY(info.fecha)} — desactualizada`}
                  sx={{
                    bgcolor: FP.color.warningSoft,
                    color: "#92400E",
                    fontWeight: 700,
                    "& .MuiChip-icon": { color: "#92400E" },
                  }}
                />
              )}
              {!sinTasa && !desactualizada && (
                <Chip
                  size="small"
                  icon={<CheckCircleOutlineIcon />}
                  label="Registrada hoy"
                  sx={{
                    bgcolor: FP.color.successSoft,
                    color: FP.color.successDark,
                    fontWeight: 700,
                    "& .MuiChip-icon": { color: FP.color.successDark },
                  }}
                />
              )}
            </Stack>
            <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, mt: 0.5 }}>
              {sinTasa
                ? `Nadie ha registrado la tasa de ${m.codigo}.`
                : `1 ${m.codigo} = ${simboloBase} ${fmtTasa(info.tasa)} · ${fechaDMY(info.fecha)}${
                    info.usuario ? ` · ${info.usuario}` : ""
                  }`}
            </Typography>
            {/* Con base USD la tasa del peso es 0.016260 y nadie reconoce ese
                número: se muestra también el inverso, que es el de la calle. */}
            {!sinTasa && toNum(info.tasa) < 1 && (
              <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
                Equivale a 1 {codigoBase} = {fmtTasa(tasaInversa(info.tasa))} {m.codigo}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              label={`Tasa de hoy (${m.codigo})`}
              value={borrador[m.codigo] ?? ""}
              onChange={(e) => setBorrador((p) => ({ ...p, [m.codigo]: e.target.value }))}
              inputProps={{ inputMode: "decimal" }}
              sx={{ ...fpInputSx, width: { xs: "100%", md: 180 } }}
            />
            <Button
              variant="contained"
              disableElevation
              disabled={guardando === m.codigo}
              onClick={() => registrarTasa(m.codigo)}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              {guardando === m.codigo ? "Guardando…" : "Registrar"}
            </Button>
            <Button
              variant="text"
              onClick={() => verHistorico(m.codigo)}
              sx={{ ...btnBase, color: FP.color.accentBlue }}
            >
              Histórico
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  };

  return (
    <Box
      component="main"
      sx={enHub ? undefined : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      <PageHeader
        compact={enHub}
        icon={CurrencyExchangeOutlinedIcon}
        title="Monedas y Tasa del Día"
        subtitle="Catálogo de monedas del sistema y la tasa a la que se opera hoy"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={enHub ? sectionBoxHub : sectionBox}>
          {cargando ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <SettingSection label="Tasa del día">
                {secundarias.length === 0 ? (
                  <Box sx={{ py: 2 }}>
                    <Typography sx={{ fontSize: "0.88rem", color: FP.color.textSecondary }}>
                      Solo está habilitada la moneda base, así que no hay tasa que registrar.
                      Habilita una moneda abajo para operar en dos monedas.
                    </Typography>
                  </Box>
                ) : (
                  secundarias.map(bloqueTasa)
                )}
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: FP.color.surfaceAlt,
                    border: `1px solid ${FP.color.border}`,
                  }}
                >
                  <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary }}>
                    <strong>Tasa mínima de venta: {fmtMoney(tasaMinima)}</strong> — es otra cosa: el
                    piso por debajo del cual Ventas x Mayor rechaza una factura. Se edita en
                    Configuración &gt; Sistema y no cambia al registrar la tasa del día.
                  </Typography>
                </Box>
              </SettingSection>

              <SettingSection label="Monedas del sistema">
                {monedas.map((m, idx) => (
                  <SettingRow
                    key={m.codigo}
                    label={`${m.codigo} · ${m.nombre}`}
                    description={
                      m.es_base === "S"
                        ? `Moneda base (${m.simbolo}). Todo se mide contra ella; no se puede desactivar.`
                        : `Símbolo ${m.simbolo} · ${m.decimales} decimales`
                    }
                    last={idx === monedas.length - 1}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      {m.es_base === "S" ? (
                        <Chip
                          size="small"
                          label="Base"
                          sx={{
                            bgcolor: FP.color.accentBlueSoft,
                            color: FP.color.accentBlue,
                            fontWeight: 700,
                          }}
                        />
                      ) : (
                        m.activo === "S" && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setCambioBase(m)}
                            sx={{ ...btnBase, color: FP.color.accentBlue }}
                          >
                            Hacer base
                          </Button>
                        )
                      )}
                      <Switch
                        checked={m.activo === "S"}
                        disabled={m.es_base === "S"}
                        onChange={(e) => cambiarActivo(m, e.target.checked)}
                      />
                    </Stack>
                  </SettingRow>
                ))}
              </SettingSection>

              {historico.codigo && (
                <SettingSection label={`Histórico de tasas · ${historico.codigo}`}>
                  {historico.filas.length === 0 ? (
                    <Typography sx={{ py: 2, fontSize: "0.88rem", color: FP.color.textSecondary }}>
                      No hay tasas registradas en los últimos 30 días.
                    </Typography>
                  ) : (
                    <Box sx={{ overflowX: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              Tasa
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Registró</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {historico.filas.map((f) => (
                            <TableRow key={f.fecha}>
                              <TableCell>{fechaDMY(f.fecha)}</TableCell>
                              <TableCell align="right">{fmtTasa(f.tasa)}</TableCell>
                              <TableCell>{f.user || "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </SettingSection>
              )}

              <Grid container>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted }}>
                    Mientras solo esté habilitada la moneda base, ninguna pantalla del sistema
                    cambia: los selectores de moneda y el campo de tasa aparecen recién cuando se
                    habilita una segunda moneda.
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Box>

      {/* Cambio de moneda base. Pide escribir el código a mano porque no es
          una preferencia: reinterpreta todo lo que ya está guardado. */}
      <Dialog open={Boolean(cambioBase)} onClose={cerrarCambioBase} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Cambiar la moneda base a {cambioBase?.codigo}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 2,
              bgcolor: FP.color.warningSoft,
              border: `1px solid ${FP.color.warning}`,
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", color: "#92400E", fontWeight: 700 }}>
              Esto no convierte ningún monto ya guardado.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#92400E", mt: 0.75 }}>
              Los costos, precios, facturas, préstamos, pagos y gastos que ya existen no llevan
              moneda: están en {codigoBase} porque {codigoBase} es hoy la base. Si la cambias a{" "}
              {cambioBase?.codigo}, esas mismas cifras pasan a leerse como {cambioBase?.codigo} sin
              que nada las recalcule — un costo de 47,970 dejaría de ser {simboloBase} 47,970 para
              leerse como {cambioBase?.simbolo} 47,970.
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, mb: 2 }}>
            Es una decisión de puesta en marcha: correcta en una empresa que aún no ha operado, o
            que sí lleva sus cifras en {cambioBase?.codigo}. Si ya tienes historial en {codigoBase},
            no lo cambies aquí — habilita {cambioBase?.codigo} como moneda secundaria y deja la base
            como está.
          </Typography>
          <TextField
            fullWidth
            size="small"
            label={`Escribe ${cambioBase?.codigo || ""} para confirmar`}
            value={confirmaTexto}
            onChange={(e) => setConfirmaTexto(e.target.value)}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={cerrarCambioBase} sx={{ ...btnBase, color: FP.color.textSecondary }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={
              cambiandoBase ||
              confirmaTexto.trim().toUpperCase() !== String(cambioBase?.codigo || "").toUpperCase()
            }
            onClick={aplicarCambioBase}
            sx={btnSolid(FP.color.warning, "#D97706")}
          >
            {cambiandoBase ? "Cambiando…" : "Cambiar la base"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
